import { useState } from "react";
import { Box, Divider, Grid, Typography } from "@mui/joy";
import { ArrowLeft, ShieldPlus } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Alert, Button, TextField } from "../../../../shared";
import { newUserValidation } from "../../../../libs/form-validations";
import { usePlatformAdmin } from "../../../../hooks/query";
import { useApiResponseParser, useAuthorization } from "../../../../hooks";
import { ROLES } from "../../../../constants/permissions";
import Breadcrumb from "../List/Breadcrumb";

export default function NewAdmin() {
    const navigate = useNavigate();
    const { parseErrorResponseData } = useApiResponseParser();
    const { hasRole } = useAuthorization();
    const isSuperAdmin = hasRole(ROLES.SUPER_ADMIN);
    const { useCreate } = usePlatformAdmin();
    const createSvc = useCreate();
    const [localError, setLocalError] = useState<string | null>(null);

    const methods = useForm({
        mode: "onChange",
        defaultValues: {
            FirstName: "",
            LastName: "",
            Email: "",
            Password: "",
        },
        resolver: newUserValidation,
    });

    const {
        handleSubmit,
        formState: { isValid },
    } = methods;

    const onSubmit = async (data: {
        FirstName: string;
        LastName: string;
        Email: string;
        Password?: string;
    }) => {
        setLocalError(null);
        try {
            await createSvc.mutateAsync({
                FirstName: data.FirstName,
                LastName: data.LastName,
                Email: data.Email,
                ...(data.Password ? { Password: data.Password } : {}),
            });
            navigate("/admin/tenant/admins");
        } catch (error) {
            setLocalError(parseErrorResponseData(error) || "Failed to create platform admin.");
        }
    };

    if (!isSuperAdmin) {
        return (
            <Box flex={1} p={3}>
                <Alert errorMessage="Only Super Admin can create platform admins." />
            </Box>
        );
    }

    return (
        <Box flex={1}>
            <Breadcrumb />
            <Divider />

            <Box p={3} pb={0} maxWidth={700}>
                <Typography level="title-lg">New Platform Admin</Typography>
                <Typography level="body-sm">
                    Create a Cognito user with Super Admin access across the platform.
                </Typography>
            </Box>

            <Box mt={2} ml={3} maxWidth={700} pb={4}>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Box display="flex" flexDirection="column" gap={1.5}>
                            <Grid container spacing={2}>
                                <Grid xs={12} sm={6}>
                                    <TextField
                                        name="FirstName"
                                        label="First Name"
                                        helperText="Admin's first name"
                                        placeholder="e.g., Jane"
                                    />
                                </Grid>
                                <Grid xs={12} sm={6}>
                                    <TextField
                                        name="LastName"
                                        label="Last Name"
                                        helperText="Admin's last name"
                                        placeholder="e.g., Doe"
                                    />
                                </Grid>
                            </Grid>

                            <TextField
                                name="Email"
                                label="Email"
                                type="email"
                                helperText="Login email. Cognito will send a temporary password if none is set below."
                                placeholder="e.g., jane@example.com"
                            />

                            <TextField
                                name="Password"
                                label="Password"
                                type="password"
                                helperText="Optional. Leave empty to email a temporary password."
                                placeholder="Optional temporary password"
                            />

                            <Box mt={1} display="flex" alignItems="center" gap={1.5}>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    color="neutral"
                                    startDecorator={<ArrowLeft size={18} />}
                                    onClick={() => navigate("/admin/tenant/admins")}
                                    disabled={createSvc.isPending}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="soft"
                                    color="primary"
                                    startDecorator={<ShieldPlus size={18} />}
                                    loading={createSvc.isPending}
                                    disabled={!isValid || createSvc.isPending}
                                >
                                    Create Admin
                                </Button>
                            </Box>

                            {(localError || createSvc.isError) && (
                                <Alert
                                    errorMessage={
                                        localError || parseErrorResponseData(createSvc.error)
                                    }
                                />
                            )}
                        </Box>
                    </form>
                </FormProvider>
            </Box>
        </Box>
    );
}
